import { ofType } from 'redux-observable';
import { concat, EMPTY, of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, filter, mergeMap } from 'rxjs/operators';

import { CACHE_AND_SERVICES } from '../../constants/playlists';
import { MULTI_PLAYLIST_ENDPOINT_URL, PLAYLIST_REQUEST_TIMEOUT } from '../../constants/request';

import { getVideoEntityTypes } from '../../helpers/chips';
import logger from '../../helpers/logger';
import { getBackupPlaylistUrl, includeFeatures, tmPostRequest } from '../../helpers/request';
import { getChannelUrl } from '../../helpers/url';
import { getFormattedVideo } from '../../helpers/video';
import * as commonSelectors from '../selectors/common';
import { channelsFetchStartAction, channelsFetchSuccessAction } from '../slices/common';

export default (action$, state$) =>
  action$.pipe(
    ofType(channelsFetchStartAction.type),
    filter(({ payload }) => !!payload.channels.length),
    mergeMap(({ payload: { cacheUsage, channels } }) => {
      const publisher = commonSelectors.publisherSelector(state$.value);
      const basePath = commonSelectors.basePathSelector(state$.value);
      const player = commonSelectors.playerSelector(state$.value);
      const tags = commonSelectors.tagsSelector(state$.value);
      const tagTypes = commonSelectors.tagTypesSelector(state$.value);

      const getUpdateChannels = (responsePlaylists = []) => {
        const allChannels = commonSelectors.channelsSelector(state$.value);

        return allChannels.map((chl) => {
          const channelUrl = getChannelUrl(basePath, chl.name);
          const item = responsePlaylists.find((item$) => channelUrl === item$.url);

          if (item?.playlistData?.playlist) {
            const { playlist, errors, ...playerPropsPassThrow } = item.playlistData;

            return {
              ...chl,
              playerPropsPassThrow,
              playlist: playlist.map((video) =>
                getFormattedVideo(video, {
                  signedUrlExp: playerPropsPassThrow.signedUrlExp,
                }),
              ),
              playlistLoaded: true,
            };
          }

          return { ...chl };
        });
      };

      const stream$ = tmPostRequest({
        url: MULTI_PLAYLIST_ENDPOINT_URL,
        timeout: PLAYLIST_REQUEST_TIMEOUT,
        body: {
          videoHub: true,
          publisherId: publisher?.slug,
          cacheUsage,
          playlists: channels.map((channel) => ({
            url: getChannelUrl(basePath, channel.name),
            widgetId: player?.name,
          })),
          videoEntityTypes: tags ? getVideoEntityTypes(tagTypes) : undefined,
          ...includeFeatures(state$.value),
        },
      }).pipe(
        mergeMap(({ response }) => {
          const allChannelsAfterUpdate = getUpdateChannels(response?.playlists);
          const actions = [];

          const unknownChannels = allChannelsAfterUpdate.filter(
            (channel) => !channel.disabled && !channel.playlistLoaded,
          );

          if (unknownChannels.length) {
            if (cacheUsage !== CACHE_AND_SERVICES) {
              actions.push(
                of(channelsFetchSuccessAction(allChannelsAfterUpdate)),
                of(
                  channelsFetchStartAction({
                    cacheUsage: CACHE_AND_SERVICES,
                    channels: unknownChannels,
                  }),
                ),
              );
            } else {
              actions.push(
                of(
                  channelsFetchSuccessAction(
                    allChannelsAfterUpdate.map((chl) => {
                      if (channels.some(({ id }) => id === chl.id)) {
                        // eslint-disable-next-line no-param-reassign
                        chl.disabled = chl.disabled || !chl.playlistLoaded;
                      }

                      return chl;
                    }),
                  ),
                ),
              );
            }
          } else {
            actions.push(of(channelsFetchSuccessAction(allChannelsAfterUpdate)));
          }

          return concat(...actions);
        }),
        catchError((e) => {
          logger.error(e);
          let channelCountLeft = channels.length;

          const streams$ = channels.map(({ id }) =>
            ajax({
              url: getBackupPlaylistUrl(publisher.slug, player.name, id),
              method: 'GET',
              crossDomain: true,
            }).pipe(
              mergeMap(({ response }) => {
                channelCountLeft -= 1;
                const allChannelsAfterUpdate = getUpdateChannels(response?.playlists);

                const channelsResult =
                  channelCountLeft === 0
                    ? allChannelsAfterUpdate.filter((channel) => channel.playlistLoaded)
                    : allChannelsAfterUpdate;

                return of(channelsFetchSuccessAction(channelsResult));
              }),
              catchError(() => {
                channelCountLeft -= 1;

                if (channelCountLeft === 0) {
                  const filteredChannels = commonSelectors
                    .channelsSelector(state$.value)
                    .filter((channel) => channel.playlistLoaded);

                  return of(channelsFetchSuccessAction(filteredChannels));
                }

                return EMPTY;
              }),
            ),
          );

          return concat(...streams$);
        }),
      );

      return concat(stream$);
    }),
  );
